import { useEffect, useMemo, useCallback } from "react";

import { useCategoryStore, useDisclosureStore, useHelperStore, useTopicStore } from "~/stores";

import { NormalizedCategory } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import CategoryItem from "./CategoryItem";

const CategoryItemList = () => {
    // Store states
    const categories = useCategoryStore(state => state.categories);
    const activeCategory = useCategoryStore(state => state.category);
    const activeDisclosure = useDisclosureStore(state => state.disclosure);
    const isFetching = useHelperStore(state => state.isLoading);

    // Store dispatch methods
    const dispatchCategory = useCategoryStore(state => state.dispatch);

    // Store methods
    const getCategoriesByProperty = useCategoryStore(state => state.getCategoriesByProperty);
    const getTopicsByParentIDs = useTopicStore(state => state.getTopicsByParentIDs);

    const disclosureId = activeDisclosure?.exploreId ?? -1;

    const handleCategoryClick = useCallback((categoryItem: NormalizedCategory) => {
        dispatchCategory({ type: SearchActions.SET_ACTIVE_CATEGORY, payload: categoryItem });
    }, [dispatchCategory]);

    //filtered an empty topics
    const filteredCategories = useMemo(() => {
        const categoriesForDisclosure = getCategoriesByProperty("disclosureId", disclosureId) ?? [];

        return categoriesForDisclosure.filter(category => {
            const topics = getTopicsByParentIDs({
                disclosureId,
                categoryId: category.exploreId
            }) ?? [];

            return topics.some(topic => topic.count > 0);
        });
    }, [categories, activeDisclosure, getCategoriesByProperty, getTopicsByParentIDs, disclosureId]);

    //re-render the list of categories base on the total/sum/empty
    const categoriesList = useMemo(() => {
        return filteredCategories.map((item) => (
            <CategoryItem
                key={item.id}
                category={item}
                isActive={activeCategory?.id === item.id}
                isFetching={isFetching}
                btnClickCallback={handleCategoryClick}
            />
        ));
    }, [filteredCategories, activeCategory, isFetching, handleCategoryClick]);

    useEffect(() => {
         if (!activeDisclosure) {
            return;
        }

        const firstCategory = filteredCategories[0] ?? null;
        
        if (!activeCategory || activeCategory.exploreId !== activeDisclosure.exploreId) {
            dispatchCategory({ type: SearchActions.SET_ACTIVE_CATEGORY, payload: firstCategory });
        }
    }, [activeDisclosure, dispatchCategory]);

    return (
        <div className="flex flex-col gap-1">
            {categoriesList}
        </div>
    );
};

export default CategoryItemList;
